/* eslint-disable ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import DynamicFieldsModel from '@linc-technologies/linced-common/models/dynamic-field/base-model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr, belongsTo, type AsyncBelongsTo } from '@warp-drive/legacy/model';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array } from 'ember-data-model-fragments/attributes';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import type Event from '@linc-technologies/linced-common/models/events/event';
import type Permission from '@linc-technologies/linced-common/models/events/permission';
import type Field from '@linc-technologies/linced-common/models/people-schema/field';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import type StoreService from '@linc-technologies/linced-common/services/store';
import { flattenList } from '@linc-technologies/linced-common/utils/fields';
import moment from 'moment/moment';

// Proto: events.PermissionReply.
export default class PermissionReply extends DynamicFieldsModel {
  declare [Type]: 'events/permission-reply';

  // Services
  @service
  declare store: StoreService;

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the school the permission is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.id')
  declare person_id: string | null | undefined;

  @belongsTo('events/event', { async: true, inverse: null })
  declare eventId: AsyncBelongsTo<Event>;

  @alias('eventId')
  declare event: AsyncBelongsTo<Event>;

  @alias('event.id')
  declare event_id: string | null | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  @alias('author.id')
  declare author_id: string | null | undefined;

  @attr('boolean', { defaultValue: false })
  declare intentionToAttend: boolean;

  @attr('string', { defaultValue: '' })
  declare caregiverComment: string;

  @array('string')
  declare fieldsToUpdate: StatefulArray<string>;

  @computed('fieldsToUpdate.[]', 'school.jurisdiction')
  get restrictedFieldsToUpdate() {
    if (this.school.get('jurisdiction') === Jurisdiction.AU_NSW) {
      // Don't allow core fields for NSW
      return this.fieldsToUpdate.filter((fieldToUpdate) => {
        return fieldToUpdate.startsWith('core:');
      });
    }
    return [];
  }

  @attr('boolean', { defaultValue: false })
  declare pending: boolean;

  @computed('createTime', 'updateTime')
  get repliedOn() {
    let time = this.createTime;
    // TS-ZERO(createTime, updateTime): server-set times with no default.
    if (this.updateTime! > this.createTime!) {
      time = this.updateTime;
    }
    return moment.unix(time!).format('Do MMMM, YYYY HH:mm:ss');
  }

  @computed('fieldsArray.[]')
  get fieldsArrayValues() {
    const fieldsArrayValues: Record<string, unknown> = {};
    if (!this.fieldsArray) {
      return fieldsArrayValues;
    }
    this.fieldsArray.forEach((field) => {
      let value;
      let permission: Permission | Field | null | undefined;
      // TS-BUG(HE-202): a field with no key throws here. linced-app
      // permission-reply-add adds one.
      // We don't know if this key refers to a school-defined permission or a person record field
      // Guess based on the structure of the key
      if (field.key!.split(':').length > 2) {
        permission = this.store.peekRecord<Permission>(
          'events/permission',
          field.key!.replaceAll(':', '-'),
        );
      } else {
        permission = this.store
          .peekAll<Field>('people-schema/field')
          .find((f) => f.key === field.key);
      }
      if (permission) {
        switch (field.datatype) {
          case DataType.STRING:
            switch (permission.inputType) {
              case InputType.RADIO:
              case InputType.SELECT:
                value = flattenList([field.valueString], permission);
                break;
              default:
                value = field.valueString;
            }
            break;
          case DataType.LIST:
            value = flattenList(field.valueList, permission);
            break;
          case DataType.BOOLEAN:
            value = field.valueBool ? 'Yes' : '';
            break;
          case DataType.INTEGER:
            value = field.valueInt;
            break;
        }
        fieldsArrayValues[field.key!] = value;
      }
    });
    return fieldsArrayValues;
  }
}
